import { format } from "date-fns";
import { Box, Paper, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import { FC, useEffect, useMemo } from 'react';
import { FaEye, FaRegCreditCard, FaTrash } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { Can } from '../../../Components/Can';
import { ConfirmDialog } from '../../../Components/Dialog/variants/dialog-confirmation/dialog-confirmation.component';
import { OrderFiltersDrawer } from '../../../Components/Drawer/variants/orders-filters/orders-filters-drawer.component';
import { ImportOrderDrawer } from '../../../Components/Drawer/variants/import-orders/import-orders-drawer.component';
import { Button } from '../../../Components/Form/Button/button.scomponent';
import { ButtonActionMenu } from '../../../Components/Form/ButtonActionsMenu/button-action-menu.component';
import { KanbanBoardOrders } from '../../../Components/KanbanBoardOrders/kanban-board-orders.component';
import { OrdersPickingToolbar } from '../../../Components/KanbanBoardOrders/orders-picking-toolbar.component';
import { TableListSkeleton } from '../../../Components/Table/table-list-skeleton.component';
import { ModalSelectOrderChoices } from '../../../Components/Modals/variants/select-order-choices/select-order-choices.component';
import { OrderStatusCell } from '../../../Components/OrderStatusCell/order-status-cell.component';
import { Placeholder } from '../../../Components/Placeholder/placeholder.component';
import { Table } from '../../../Components/Table/table.component';
import { Tag } from '../../../Components/Tag/tag.component';
import { CustomTabPanel } from '../../../Components/Tabs/CustomTabPanel/custom-tab-panel.component';
import { BaseContentPage } from '../../../Components/Templates/BaseContentPage/base-content-page.componen';
import { BaseScreen } from '../../../Components/Templates/BaseScreen/basescreen.component';
import { OrderProductionStatusEnum, OrderStatusEnum } from '../../../Services/api/orders/orders.interface';
import { CancelOrderUseCase } from '../../../Usecase/Orders/cancel-order-usecase';
import { ExportInPickingOrderUseCase } from '../../../Usecase/Orders/export-in-picking-order.usecase';
import { ImportOrderUseCase } from '../../../Usecase/Orders/import-order-usecase';
import { ListOrdersUseCase } from '../../../Usecase/Orders/list-orders-usecase';
import { PickingFlowUseCase } from '../../../Usecase/Orders/picking-flow.usecase';
import { ShowOrdersUseCase } from '../../../Usecase/Orders/show-order-usecase';
import { UpdateOrdersUseCase } from '../../../Usecase/Orders/update-order-usecase';
import { ReprocessOrderPaymentsUseCase } from '../../../Usecase/OrderPayments/reprocess-order-payments-usecase';
import { GetOrderAuditTimelineUseCase } from '../../../Usecase/Audit/get-order-audit-timeline-usecase';
import { ListProductUseCase } from '../../../Usecase/Products/list-product-usecase';
import { FormatMoney } from '../../../utils/formatMoney';
import { normalizeOrderStatusColors, normalizeOrderStatusTitle } from '../../../utils/normalizeOrderStatus';
import { useListOrders } from './orders-list.hook';
import { IoAddOutline, IoFilterOutline } from 'react-icons/io5';

interface IOrdersList {
    listProductUseCase: ListProductUseCase
    listOrdersUseCase: ListOrdersUseCase
    reprocessOrderPaymentsUseCase: ReprocessOrderPaymentsUseCase
    pickingFlowUseCase: PickingFlowUseCase
    UpdateOrdersUseCase: UpdateOrdersUseCase
    exportInPickingOrderUseCase: ExportInPickingOrderUseCase
    showOrdersUseCase: ShowOrdersUseCase
    cancelOrderUseCase: CancelOrderUseCase
    importOrderUseCase: ImportOrderUseCase
    getOrderAuditTimelineUseCase: GetOrderAuditTimelineUseCase
}

const CellText: FC<{ value?: string | null }> = ({ value }) => {
  const text = value?.trim() || '—'

  return (
    <Tooltip title={text !== '—' ? text : ''} placement="top-start" enterDelay={400}>
      <Typography
        variant="body2"
        component="span"
        sx={{
          display: 'block',
          width: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          lineHeight: 1.25,
          fontSize: '0.8125rem',
        }}
      >
        {text}
      </Typography>
    </Tooltip>
  )
}

const ORDER_DETAILS_FROM = 'orders-list'

const buildOrderDetailsUrl = (orderId: string) =>
  `/financial-details/${orderId}?from=${ORDER_DETAILS_FROM}`

const formatOrderDate = (value?: string | null) => {
  if (!value) return '—'
  try {
    return format(new Date(value), 'dd/MM/yyyy')
  } catch {
    return value
  }
}

const OrdersList : FC<IOrdersList> = ({
  listProductUseCase,
  listOrdersUseCase,
  reprocessOrderPaymentsUseCase,
  pickingFlowUseCase,
  UpdateOrdersUseCase,
  exportInPickingOrderUseCase,
  showOrdersUseCase,
  cancelOrderUseCase,
  importOrderUseCase,
  getOrderAuditTimelineUseCase,
}) => {
    const { 
        list, 
        setShowModalChoices, 
        showModalChoices, 
        listLoading,
        cancellingLoading,
        pickingLoading,
        loadError,
        loadErrorCancelling,
        listOrders,
        listOrdersCancelling,
        totalRegisters, 
        setShowFiltersModal, 
        showFiltersModal,
        currentFilters,
        handleFilterSelect,
        handleReprocessPayment,
        orderToReprocess,
        setOrderToReprocess,
        handleChange,
        a11yProps,
        value,
        kanbanSchema,
        handleUpdateOrderStatus,
        handlePrintPickingList,
        handleCardClick,
        orderDetailsData,
        orderToCancel,
        setOrderToCancel,
        handleCancelOrder,
        hasCancelPermission,
        listCancelling,
        totalRegistersCancelling,
        handleImportOrder,
        showImportOrderModal,
        setShowImportOrderModal,
        handlePageChangeOrders,
        handlePageChangeCancelling,
        handleSearch,
        handlePickingSearch,
        refreshPickingFlow,
        clearPickingSearch,
        currentFiltersPickingFlow,
        pickingTotalCount,
        handleSortModelChange,
        handleSortModelChangeCancelling,
        ordersPage,
        cancellingPage,
        ordersSortModel,
        cancellingSortModel,
        currentFiltersCancelling,
    } = useListOrders({
        listProductUseCase,
        listOrdersUseCase,
        reprocessOrderPaymentsUseCase,
        pickingFlowUseCase,
        UpdateOrdersUseCase,
        exportInPickingOrderUseCase,
        showOrdersUseCase,
        cancelOrderUseCase,
        importOrderUseCase,
    })

    const currentSearch = currentFilters.searchBy ?? ''

    const orderToReprocessLabel = useMemo(() => {
      if (!orderToReprocess) return ''
      const order = list?.find((item) => item.id === orderToReprocess)
      return order?.nsOrderId ? `#${order.nsOrderId}` : orderToReprocess
    }, [orderToReprocess, list])

    const columnsListing = useMemo(() => {
        const createColumns: any[] = [ 
          {
            field: 'nsOrderId',
            headerName: 'Pedido',
            width: 130,
            sortable: true,
            renderCell: (params: any) => (
              <Box
                component={Link}
                to={buildOrderDetailsUrl(params.row.id)}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: 'primary.main',
                  fontWeight: 600,
                  textDecoration: 'none',
                  display: 'block',
                  width: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  lineHeight: 1.25,
                  fontSize: '0.8125rem',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                {params.value ? `#${params.value}` : '—'}
              </Box>
            ),
          },
          {
            field: 'store',
            headerName: 'Loja',
            flex: 0.8,
            minWidth: 90,
            headerAlign: 'center',
            align: 'center',
            sortable: true,
            renderCell: (params: any) => <CellText value={params?.value || 'Não informado'} />,
          },
          {
            field: 'createdAt',
            headerName: 'Data',
            width: 110,
            sortable: true,
            renderCell: (params: any) => <CellText value={formatOrderDate(params.row?.createdAt)} />,
          },
          {
            field: 'customer',
            headerName: 'Cliente',
            flex: 1.2,
            minWidth: 160,
            sortable: true,
            valueGetter: (_value: unknown, row: any) => row?.customer?.name,
            renderCell: (params: any) => <CellText value={params?.row?.customer?.name || 'Não informado'} />,
          },
          {
            field: 'totalValue',
            headerName: 'Total',
            width: 120,
            sortable: true,
            type: 'number',
            valueGetter: (_value: unknown, row: any) => Number(row?.totalValue) || 0,
            renderCell: (params: any) => <CellText value={FormatMoney(Number(params?.row?.totalValue || 0))} />,
          },
          {
            field: 'observations',
            headerName: 'Observações',
            flex: 1,
            minWidth: 140,
            renderCell: (params: any) => <CellText value={params?.value || '—'} />,
          },
          {
            field: 'status',
            headerName: 'Status',
            headerAlign: 'center',
            flex: 1,
            minWidth: 240,
            sortable: false,
            renderCell: (params: any) => (
              <OrderStatusCell 
                status={params.row.status}
                productionStatus={params.row.productionStatus}
                errorMessage={params.row.errorMessage}
              />
            ),
          },
          {
            field: 'actions',
            headerName: 'Ações',
            headerAlign: 'center',
            align: 'center',
            width: 72,
            sortable: false,
            renderCell: (params: any) => (
              <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                <ButtonActionMenu
                  options={[
                    {
                      title: 'Ver detalhes',
                      href: buildOrderDetailsUrl(params.row.id),
                      openInNewTab: true,
                      icon: <FaEye size={14} />,
                    },
                    {
                      title: 'Reprocessar pagamento',
                      onClick: () => setOrderToReprocess(params.row.id),
                      icon: <FaRegCreditCard size={14} />,
                    },
                  ]}
                />
              </Box>
            ),
          },
        ];
      
        return createColumns;
    }, [setOrderToReprocess]);

    const columnsCancelling = useMemo(() => {
      const createColumns: any[] = [ 
        {
          field: 'nsOrderId',
          headerName: 'Pedido',
          width: 130,
          sortable: true,
          renderCell: (params: any) => (
            <Box
              component={Link}
              to={buildOrderDetailsUrl(params.row.id)}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: 'primary.main',
                fontWeight: 600,
                textDecoration: 'none',
                display: 'block',
                width: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                lineHeight: 1.25,
                fontSize: '0.8125rem',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              {params.value ? `#${params.value}` : '—'}
            </Box>
          ),
        },
        {
          field: 'store',
          headerName: 'Loja',
          flex: 0.8,
          minWidth: 90,
          headerAlign: 'center',
          align: 'center',
          sortable: true,
          renderCell: (params: any) => <CellText value={params?.value || 'Não informado'} />,
        },
        {
          field: 'createdAt',
          headerName: 'Data',
          width: 110,
          sortable: true,
          renderCell: (params: any) => <CellText value={formatOrderDate(params.row?.createdAt)} />,
        },
        {
          field: 'customer',
          headerName: 'Cliente',
          flex: 1.2,
          minWidth: 160,
          sortable: true,
          valueGetter: (_value: unknown, row: any) => row?.customer?.name,
          renderCell: (params: any) => <CellText value={params?.row?.customer?.name || 'Não informado'} />,
        },
        {
          field: 'totalValue',
          headerName: 'Total',
          width: 120,
          sortable: true,
          type: 'number',
          valueGetter: (_value: unknown, row: any) => Number(row?.totalValue) || 0,
          renderCell: (params: any) => <CellText value={FormatMoney(Number(params?.row?.totalValue || 0))} />,
        },
        {
          field: 'observations',
          headerName: 'Observações',
          flex: 1,
          minWidth: 140,
          renderCell: (params: any) => <CellText value={params?.value || '—'} />,
        },
        {
          field: 'payment_status',
          headerName: 'Status',
          headerAlign: 'center',
          flex: 1,
          minWidth: 200,
          sortable: false,
          renderCell: (params: any) => (
            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              {params?.row?.productionStatus === OrderProductionStatusEnum.PRODUCTION && (
                <Tag 
                  title='Aguardando produção' 
                  boxStyle={{ backgroundColor: 'rgba(110, 197, 5, 0.73)', padding: '8px 12px', whiteSpace: 'nowrap' }} 
                  titleStyle={{ color: '#FFFFFF', fontSize: '0.8125rem', textAlign: 'center', whiteSpace: 'nowrap' }} 
                />
              )}
              {!['DEAD'].includes(params.row.status) && params.row.status !== OrderStatusEnum.INVOICE_PROBLEM && params?.row?.productionStatus !== OrderProductionStatusEnum.PRODUCTION && (
                <Tag 
                  title={normalizeOrderStatusTitle(params.row.status as OrderStatusEnum)} 
                  boxStyle={{ 
                    backgroundColor: normalizeOrderStatusColors(params.row.status as OrderStatusEnum)?.bgColor, 
                    padding: '8px 12px',
                    whiteSpace: 'nowrap',
                  }} 
                  titleStyle={{ 
                    color: normalizeOrderStatusColors(params.row.status as OrderStatusEnum)?.textColor, 
                    fontSize: '0.8125rem', 
                    textAlign: 'center',
                    whiteSpace: 'nowrap',
                  }} 
                />
              )}
            </Box>
          ),
        },
        {
          field: 'actions',
          headerName: 'Ações',
          headerAlign: 'center',
          align: 'center',
          width: 72,
          sortable: false,
          renderCell: (params: any) => (
            <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
              <ButtonActionMenu
                options={[
                  ...(hasCancelPermission ? [{
                    title: 'Cancelar pedido',
                    onClick: () => setOrderToCancel(params.row),
                    icon: <FaTrash size={14} />,
                    titleColor: '#d32f2f',
                  }] : []),
                ]}
              />
            </Box>
          ),
        },
      ];
    
      return createColumns;
    }, [hasCancelPermission, setOrderToCancel]);

    const handleSearchOrders = (data: { page: number; searchBy?: string }) => {
      if (value === 1) {
        handlePickingSearch({ searchBy: data.searchBy });
        return;
      }

      handleSearch({ ...currentFilters, searchBy: data.searchBy, page: data.page });
    };

    const searchPlaceholder = value === 1
      ? 'Buscar pedido, cliente ou transportadora na separação B2B'
      : 'Pesquisar por número, cliente ou loja';

    return (
      <Can permissions={['menu:orders:manage']}>
        <ConfirmDialog
          title='Reprocessar Pagamento'
          onCancel={() => setOrderToReprocess('')}
          description={`Tem certeza que deseja reprocessar o pagamento do pedido ${orderToReprocessLabel || 'selecionado'}?`}
          open={!!orderToReprocess}
          onConfirm={() => handleReprocessPayment(orderToReprocess || '')}
        />
        <ConfirmDialog
          title='Cancelar Pedido'
          onCancel={() => setOrderToCancel(undefined)}
          description={`Tem certeza que deseja cancelar o pedido ${orderToCancel?.nsOrderId ? `#${orderToCancel.nsOrderId}` : 'selecionado'}?`}
          open={!!orderToCancel}
          onConfirm={handleCancelOrder}
          input={{ label: 'Motivo', placeholder: 'Preencha o motivo', required: false }}
        />

        <BaseScreen>
          <Paper sx={{ borderRadius: 2, overflow: 'hidden', width: '100%' }}>
            {showModalChoices && <ModalSelectOrderChoices handleClose={() => setShowModalChoices(false)} />}
            
            <OrderFiltersDrawer 
              handleSelect={handleFilterSelect} 
              open={showFiltersModal} 
              title='Filtros' 
              subTitle={value === 1 ? 'Filtros aplicados também na separação B2B' : 'Refine a listagem de pedidos'}
              onToggle={() => setShowFiltersModal(prev => !prev)} 
            />

            <ImportOrderDrawer 
              handleSelect={handleImportOrder} 
              open={showImportOrderModal} 
              title='Importar Pedido' 
              onToggle={() => setShowImportOrderModal(prev => !prev)} 
            />
          
            <BaseContentPage 
                title='Pedidos'
                subtitle='Acompanhe pedidos, separação B2B e cancelamentos'
                breadcrumb={[{ title: 'Pedidos', link: '/orders-list' }, { title: 'Listagem' }]}
                minHeight='auto'
                search
                searchDefaultValue={currentSearch}
                searchFunction={handleSearchOrders}
                searchLoading={(listLoading || cancellingLoading) && (!!list?.length || !!listCancelling?.length)}
                placeholderSearch={searchPlaceholder}
                Action={() => (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: 1.5,
                      width: { xs: '100%', md: 'auto' },
                      flexShrink: 0,
                    }}
                  >
                    <Button
                      variant='outlined'
                      LeftIcon={() => <IoAddOutline size={18} />}
                      sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: { sm: 150 }, flexShrink: 0 }}
                      boxLeftSx={{ gap: '8px', marginLeft: '4px' }}
                      textSx={{ textTransform: 'none', fontWeight: 500, whiteSpace: 'nowrap' }}
                      onClick={() => setShowImportOrderModal(true)}
                    >
                      Importar pedido
                    </Button>
                    <Button
                      variant='outlined'
                      LeftIcon={() => <IoFilterOutline size={18} />}
                      sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: { sm: 100 }, flexShrink: 0 }}
                      boxLeftSx={{ gap: '8px', marginLeft: '4px' }}
                      textSx={{ textTransform: 'none', fontWeight: 500, whiteSpace: 'nowrap' }}
                      onClick={() => setShowFiltersModal(true)}
                    >
                      Filtros
                    </Button>
                  </Box>
                )}
            >
              <Tabs value={value} onChange={handleChange} sx={{ mb: 1 }}>
                <Tab label="Lista" {...a11yProps(0)} />
                <Tab label="Separação B2B" {...a11yProps(1)} />
                <Tab label="Cancelamento" {...a11yProps(2)} />
              </Tabs>

              <CustomTabPanel value={value} index={0}>
                {!!list?.length ? (
                  <Box sx={{ width: '100%', minWidth: 0 }}>
                    <Table
                      data={list}
                      columns={columnsListing}
                      totalCount={totalRegisters}
                      search={currentSearch}
                      page={ordersPage}
                      sortModel={ordersSortModel}
                      sortingOrder={['asc', 'desc']}
                      onPageChange={handlePageChangeOrders}
                      onSortModelChange={handleSortModelChange}
                      sortingMode="server"
                      rowHeight={72}
                      stickyHeader
                      maxHeight="calc(100vh - 250px)"
                    />
                  </Box>
                ) : listLoading ? (
                  <TableListSkeleton rowHeight={72} />
                ) : loadError ? (
                  <Placeholder
                    description="Não foi possível carregar os pedidos."
                    onRetry={() => listOrders({ ...currentFilters, page: ordersPage })}
                  />
                ) : (
                  <Placeholder description="Nenhum pedido encontrado." />
                )}
              </CustomTabPanel>

              <CustomTabPanel value={value} index={1}>
                <OrdersPickingToolbar
                  filters={{
                    ...currentFiltersPickingFlow,
                    searchBy: currentSearch || currentFiltersPickingFlow.searchBy,
                    startDate: currentFilters.startDate,
                    endDate: currentFilters.endDate,
                  }}
                  totalOrders={pickingTotalCount}
                  loading={pickingLoading}
                  onRefresh={refreshPickingFlow}
                  onPrint={handlePrintPickingList}
                  onClearSearch={clearPickingSearch}
                />
                <KanbanBoardOrders 
                  initialData={kanbanSchema} 
                  handleSelectProductToBill={() => {}} 
                  productsToBill={[]} 
                  setOmieOrderData={() => {}} 
                  canClick={true} 
                  canDrag={true} 
                  handleUpdateOrderStatus={handleUpdateOrderStatus}
                  onPrintPickingList={handlePrintPickingList}
                  onCardClick={handleCardClick}
                  orderDetailsData={orderDetailsData}
                  getOrderAuditTimelineUseCase={getOrderAuditTimelineUseCase}
                />
              </CustomTabPanel>

              <CustomTabPanel value={value} index={2}>
                {!!listCancelling?.length ? (
                  <Box sx={{ width: '100%', minWidth: 0 }}>
                    <Table
                      data={listCancelling}
                      columns={columnsCancelling}
                      totalCount={totalRegistersCancelling}
                      search={currentSearch}
                      page={cancellingPage}
                      sortModel={cancellingSortModel}
                      sortingOrder={['asc', 'desc']}
                      onPageChange={handlePageChangeCancelling}
                      onSortModelChange={handleSortModelChangeCancelling}
                      sortingMode="server"
                      rowHeight={56}
                      stickyHeader
                      maxHeight="calc(100vh - 250px)"
                    />
                  </Box>
                ) : cancellingLoading ? (
                  <TableListSkeleton rowHeight={56} />
                ) : loadErrorCancelling ? (
                  <Placeholder
                    description="Não foi possível carregar os pedidos em cancelamento."
                    onRetry={() => listOrdersCancelling({ ...currentFiltersCancelling, page: cancellingPage })}
                  />
                ) : (
                  <Placeholder description="Nenhum pedido em cancelamento encontrado." />
                )}
              </CustomTabPanel>
            </BaseContentPage>
          </Paper>          
        </BaseScreen>
      </Can>
    )
}

export { OrdersList };
